import { Box, Container, Typography, Stack } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { BannerType } from 'types';

import BannerImg from 'assets/images/bannerv3.png';
import pattern from 'assets/images/Pattern.png';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Icon from 'assets/images/pagenotfound.png';
import video from './video.mp4';

const GRADIENT: { [key: string]: string } = {
    purple: 'linear-gradient(90deg, #612057 0%, #6F358D 100%)',
    blue: 'linear-gradient(90.01deg, #0074A0 0.01%, #007EAF 111.87%)',
    green: 'linear-gradient(90.01deg, #00875E 0.01%, #009B6C 111.87%)',
    page404: 'linear-gradient(90deg, #612057 0%, #6F358D 100%)'
};

const Wrapper = styled(Box)({
    position: 'relative',
    color: 'white'
});

const TagsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    mb: '8px',
    flexWrap: 'wrap',
    gap: '10px',
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}));

const StyledContainer = styled(Container)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    padding: '64px 16px',
    position: 'relative',
    [theme.breakpoints.between('xs', 'sm')]: {
        paddingBottom: '100px',
        justifyContent: 'flex-start'
    },
    [theme.breakpoints.down('md')]: {
        padding: '16px 16px',
    },
    flex: 1
}));

const Title = styled(Typography)({
    fontWeight: 700,
    fontSize: '49px',
    lineHeight: '56px'
});

const SubTitle = styled(Typography)({
    fontSize: '18px',
    lineHeight: '28px'
});

const Duration = styled(Typography)({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: '12px'
});

const PatternContainer = styled(Box)({
    height: '100%',
    width: '100%',
    maxWidth: '950px',
    position: 'absolute',
    bottom: 0,
    left: 0,
    background: `url(${pattern})`,
    backgroundRepeat: 'no-repeat',
    backgroundOrigin: 'content-box',
    backgroundSize: 'cover',
    mixBlendMode: 'multiply'
});

const Tag = styled('span')({
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '11px',
    padding: '4px 6px',
    border: '1px solid #FFFFFF75',
    borderRadius: '3px'
});

const Image = styled('img')({
    height: '80px',
    width: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '10px'
});

function Banner({ tags, title, subTitle, type, duration, title2 }: BannerType) {
    const theme = useTheme();
    const isHome = Boolean(type === 'home');
    const is404 = Boolean(type === 'page404');
    const durationStr = `${duration} minutes`;
    const wrapperSX = isHome
        ? {
            display: 'flex',
            height: '328px',
            position: 'relative',
            [theme.breakpoints.down('sm')]: {
                height: '250px'
            },
            '& .div': {
                [theme.breakpoints.down('md')]: {
                    marginRight: '0px'
                }
            }
        }
        : {
            background: GRADIENT[type]
        };
    const TitleSX = is404
        ? {
            textAlign: 'center',
            marginBottom: '15px',
            [theme.breakpoints.down('md')]: {
                fontSize: '32px',
                lineHeight: '42px'
            }
        }
        : {
            textAlign: 'start',
            marginBottom: '0px',
            [theme.breakpoints.down('md')]: {
                fontSize: '32px',
                lineHeight: '42px'
            },
            position: 'relative',
            zIndex: 9999
        };
    const subTitleSX = is404
        ? {
            textAlign: 'center',
            marginBottom: '50px',
            position: 'relative',
            zIndex: 9999,
            [theme.breakpoints.down('md')]: {
                fontSize: '16px',
                lineHeight: '26px'
            }
        }
        : {
            textAlign: 'start',
            marginBottom: '0px',
            position: 'relative',
            zIndex: 9999,
            [theme.breakpoints.down('md')]: {
                fontSize: '16px',
                lineHeight: '26px'
            }
        };

    return (
        <Wrapper data-testid="banner" sx={wrapperSX}>
            {!isHome && <PatternContainer />}
            <StyledContainer
                sx={{
                    position: 'relative',
                    zIndex: 300
                }}
            >
                {!!tags && (
                    <TagsContainer>
                        {tags?.map((tag: string) => (
                            <Tag key={`tag-${tag}`}>{tag}</Tag>
                        ))}
                    </TagsContainer>
                )}
                {String(type) === 'page404' && <Image src={Icon} />}
                <Title sx={TitleSX}>{title}</Title>
                <Title sx={TitleSX}>{title2}</Title>
                <SubTitle sx={subTitleSX}>{subTitle}</SubTitle>
                {!!duration && (
                    <Duration>
                        <AccessTimeIcon fontSize="small" sx={{ mr: '10px' }} /> {durationStr}
                    </Duration>
                )}
            </StyledContainer>
            {isHome && (
                <Box
                    sx={{
                        flex: 1,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        zIndex: 200,
                        opacity: {
                            lg: 1,
                            xs: 0.9
                        },
                        backgroundImage: `url(${BannerImg}) `,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'repeat',
                        height: '100%',
                        width: '100%'
                    }}
                />
            )}
            {isHome && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        zIndex: 1,
                        opacity: 1,
                        width: {
                            xxl: '55vw',
                            xl: '52vw',
                            lg: '52vw',
                            xs: '100vw'
                        },
                        height: '100%'
                    }}
                >
                    <video
                        height="100%"
                        width="100%"
                        controls={false}
                        loop
                        autoPlay
                        muted
                        playsInline
                        style={{ objectFit: 'fill', objectPosition: '50% 50%' }}
                    >
                        <source src="https://dltoxo3jo9zve.cloudfront.net/GettyImages-1335450116.mp4" type="video/mp4" />
                    </video>
                </Box>
            )}
        </Wrapper>
    );
}
export default Banner;
